import React, { useState, useEffect } from 'react';
import { Save, X, KeyRound, Check, Loader2 } from 'lucide-react';
import {
  FarmerDto,
  CreateFarmerDto,
  UpdateFarmerDto,
  farmerAccountApi,
  FarmerAccountDto,
} from '../../../services/api';

interface FarmerFormProps {
  farmer?: FarmerDto;
  onSave: (data: CreateFarmerDto | UpdateFarmerDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export const FarmerForm: React.FC<FarmerFormProps> = ({
  farmer,
  onSave,
  onCancel,
  isLoading,
}) => {
  const [formData, setFormData] = useState<CreateFarmerDto & { isActive?: boolean }>({
    name: farmer?.name || '',
    externalId: farmer?.externalId || '',
    isActive: farmer?.isActive ?? true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Nombre del Agricultor *
        </label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="ej: Pepe García"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          ID Externo *
        </label>
        <input
          type="text"
          required
          value={formData.externalId}
          onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
          placeholder="ej: AGR001"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
        <p className="text-xs text-slate-500 mt-1">
          Identificador único del agricultor en el sistema externo
        </p>
      </div>

      {farmer && (
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-slate-700">Agricultor activo</span>
          </label>
        </div>
      )}

      {farmer && <FarmerAccountSection farmerId={farmer.id} farmerName={farmer.name} />}

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Guardando...
            </>
          ) : (
            <>
              <Save size={18} />
              {farmer ? 'Actualizar' : 'Crear'} Agricultor
            </>
          )}
        </button>
      </div>
    </form>
  );
};

interface FarmerAccountSectionProps {
  farmerId: string;
  farmerName: string;
}

const FarmerAccountSection: React.FC<FarmerAccountSectionProps> = ({
  farmerId,
  farmerName,
}) => {
  const [account, setAccount] = useState<FarmerAccountDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    farmerAccountApi
      .get(farmerId)
      .then((data) => {
        if (!cancelled) setAccount(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'No se pudo cargar el estado de la cuenta');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [farmerId]);

  const handleCreate = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!/^\d{4,6}$/.test(pin)) {
      setError('El PIN debe tener entre 4 y 6 dígitos numéricos');
      return;
    }
    if (phone.trim().length < 6) {
      setError('Introduce un teléfono válido');
      return;
    }

    try {
      setSubmitting(true);
      await farmerAccountApi.create({
        farmerId,
        phone: phone.trim(),
        pin: pin.trim(),
        fullName: farmerName,
      });
      const updated = await farmerAccountApi.get(farmerId);
      setAccount(updated);
      setSuccessMsg('Cuenta creada. El agricultor ya puede entrar en la PWA.');
      setPhone('');
      setPin('');
    } catch (err: any) {
      setError(err.message || 'No se pudo crear la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound size={18} className="text-slate-600" />
        <h3 className="text-sm font-semibold text-slate-800">Cuenta de acceso</h3>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" />
          Comprobando…
        </div>
      )}

      {!loading && account?.exists && (
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Check size={16} className="text-green-600" />
          <span>
            Acceso activo con el teléfono <strong>{account.phone}</strong>.
          </span>
        </div>
      )}

      {!loading && account && !account.exists && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Crea las credenciales para que este agricultor pueda entrar en la PWA.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Teléfono
              </label>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+34666123456"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                PIN inicial (4–6 dígitos)
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{4,6}"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="1234"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm tracking-widest"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={submitting}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <KeyRound size={14} />
            )}
            Crear cuenta de acceso
          </button>
        </div>
      )}

      {successMsg && (
        <p className="mt-3 text-xs text-green-700 bg-green-50 px-3 py-2 rounded">
          {successMsg}
        </p>
      )}
      {error && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 px-3 py-2 rounded flex items-center gap-1">
          <X size={14} />
          {error}
        </p>
      )}
    </div>
  );
};
